import {
  FrequencyType,
  KeyDatesEnum,
  ProductTypeEnum,
} from '../model/enums/product-capture.enums';

import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { NgbDateTimeStruct } from 'src/app/ui-kit/dc-date-input/date-input.consts';
import { DateUtils } from 'src/app/utils/date.utils';
import { PRODUCT_POST_DATA } from '../create-product/create-product.const';
import { PayoffSchedule } from '../model/interfaces/payoff-schedule.interface';
import { ProductCaptureData } from '../model/interfaces/product-capture.interface';
import { MapPayoffScheduleDatesToString } from './map-payoff-schedule-dates-to-string';

export function GetProductCapturePostData(
  post: ProductCaptureData
): ProductCaptureData {
  const getOffset = (keyName) =>
    post.summary.keyDates[keyName].offset === FrequencyType.TENOR
      ? post.summary.tenor
      : post.summary.keyDates[keyName].offset;

  let output = {
    ...post,
    summary: {
      ...post.summary,
      schedule: {
        scheduleStrategy: post.summary.schedule.scheduleStrategy,
        coupon: {
          ...post.summary.schedule.coupon,
          schedule: MapPayoffScheduleDatesToString(
            post.summary.schedule?.coupon?.schedule as PayoffSchedule[],
            post.payoff.coupon?.couponType
          ),
        },
        earlyRedemption: {
          ...post.summary.schedule.earlyRedemption,
          schedule: MapPayoffScheduleDatesToString(
            post.summary.schedule?.earlyRedemption?.schedule as PayoffSchedule[],
            post.payoff.callability?.callEventType
          ),
        },
        participation: {
          upside: {
            ...post.summary.schedule?.participation.upside,
            schedule: MapPayoffScheduleDatesToString(
              post.summary.schedule?.participation.upside?.schedule as PayoffSchedule[],
              post.payoff.participation?.upside?.type
            ),
          },
          downside: {
            ...post.summary.schedule?.participation.downside,
            schedule: MapPayoffScheduleDatesToString(
              post.summary.schedule?.participation.downside?.schedule as PayoffSchedule[],
              post.payoff.participation?.downside?.downsideType
            ),
          },
          base: {
            ...post.summary.schedule?.participation.base,
            schedule: MapPayoffScheduleDatesToString(
              post.summary.schedule?.participation.base?.schedule as PayoffSchedule[],
              post.payoff.participation?.base?.type
            ),
          },
        },
      },
      keyDates: {
        issueDate: {
          ...post.summary.keyDates.issueDate,
          offset: getOffset(KeyDatesEnum.issueDate),
          value: DateUtils.getStringFromDateObject(
            post.summary.keyDates.issueDate.value as unknown as NgbDate
          ),
        },
        ...(post.summary.keyDates.interestStartDate && {
          interestStartDate: {
            ...post.summary.keyDates.interestStartDate,
            offset: getOffset(KeyDatesEnum.interestStartDate),
            value: DateUtils.getStringFromDateObject(
              post.summary.keyDates.interestStartDate
                .value as unknown as NgbDate
            ),
          },
        }),
        ...(post.summary.keyDates.interestEndDate && {
          interestEndDate: {
            ...post.summary.keyDates.interestEndDate,
            offset: getOffset(KeyDatesEnum.interestEndDate),
            value: DateUtils.getStringFromDateObject(
              post.summary.keyDates.interestEndDate
                .value as unknown as NgbDate
            ),
          },
        }),
        initialFixingDate: {
          ...post.summary.keyDates.initialObservationDate,
          offset: getOffset(KeyDatesEnum.initialObservationDate),
          value: DateUtils.getStringFromDateObject(
            post.summary.keyDates.initialObservationDate
              .value as unknown as NgbDate
          ),
        },
        maturityDate: {
          ...post.summary.keyDates.redemptionDate,
          offset: getOffset(KeyDatesEnum.redemptionDate),
          value: DateUtils.getStringFromDateObject(
            post.summary.keyDates.redemptionDate.value as unknown as NgbDate
          ),
        },
        finalFixingDate: {
          ...post.summary.keyDates.finalObservationDate,
          offset: getOffset(KeyDatesEnum.finalObservationDate),
          value: DateUtils.getStringFromDateObject(
            post.summary.keyDates.finalObservationDate
              .value as unknown as NgbDate
          ),
        },
        tradeDate: {
          ...post.summary.keyDates.tradeDate,
          offset: getOffset(KeyDatesEnum.tradeDate),
          value: DateUtils.getStringFromDateObject(
            post.summary.keyDates.tradeDate.value as unknown as NgbDate
          ),
        },
        dtePmtUnadjFirstT: {
          ...post.summary.keyDates.dtePmtUnadjFirstT,
          offset: getOffset(KeyDatesEnum.dtePmtUnadjFirstT),
          value: DateUtils.getStringFromDateObject(
            post.summary.keyDates.dtePmtUnadjFirstT.value as unknown as NgbDate
          ),
        },
        dtePmtUnadjLast0: {
          ...post.summary.keyDates.dtePmtUnadjLast0,
          offset: getOffset(KeyDatesEnum.dtePmtUnadjLast0),
          value: DateUtils.getStringFromDateObject(
            post.summary.keyDates.dtePmtUnadjLast0.value as unknown as NgbDate
          ),
        },
        productFinalisationDate: {
          ...post.summary.keyDates.productFinalisationDate,
          offset: getOffset(KeyDatesEnum.productFinalisationDate),
          value: DateUtils.getStringFromDateObject(
            post.summary.keyDates.productFinalisationDate
              .value as unknown as NgbDate
          ),
        },
      },
      settlementRollConvention: post.summary.settlementRollConvention?.length
        ? post.summary.settlementRollConvention
        : null,
      tradingRollConvention: post.summary.tradingRollConvention?.length
        ? post.summary.tradingRollConvention
        : null,
      accrualRollConvention: post.summary.accrualRollConvention?.length
        ? post.summary.accrualRollConvention
        : null,
    },
    payoff: {
      ...post.payoff,
      participation: {
        ...post.payoff.participation,
        upside: {
          ...post.payoff.participation.upside,
          rebateDate: DateUtils.getStringFromDateObject(
            post.payoff.participation.upside.rebateDate as unknown as NgbDate
          ),
        },
      },
    },
    sales: {
      ...post.sales,
      feesGroup: {
        feesArr: post.sales.feesGroup.feesArr?.filter(
          (fee) =>
            fee.feeType !== null &&
            fee.amountFee !== '' &&
            fee.currencyFee !== null
        ),
      },
      targetMarkets: [
        ...post.sales.targetMarkets.filter(
          (targetMarket) =>
            !(
              targetMarket.clientType == null &&
              targetMarket.distributionStrategy == null
            )
        ),
      ],
      contactInformation: [
        ...post.sales.contactInformation.filter(
          (contact) => contact.contactId !== null
        ),
      ],
    },
    legalForm: {
      ...post.legalForm,
      countriesOfOffer: {
        countriesOfOfferArr: post.legalForm.countriesOfOffer.countriesOfOfferArr
          ?.filter(
            (countriesOfOfferRow) =>
              countriesOfOfferRow.jurisdictionsLegal !== null
          )
          .map((countriesRow) => {
            countriesRow.subscriptions = countriesRow.subscriptions.map(
              (subRow) => ({
                subStartDate: DateUtils.getStringWithOffsetFromDateTimeObject(
                  subRow.subStartDate as unknown as NgbDateTimeStruct,
                  subRow.timezone
                ),
                subEndDate: DateUtils.getStringWithOffsetFromDateTimeObject(
                  subRow.subEndDate as unknown as NgbDateTimeStruct,
                  subRow.timezone
                ),
                isaEndDate: DateUtils.getStringWithOffsetFromDateTimeObject(
                  subRow.isaEndDate as unknown as NgbDateTimeStruct,
                  subRow.timezone
                ),
                timezone: subRow.timezone,
                distributionChannel: subRow.distributionChannel,
              })
            );
            return countriesRow;
          }),
      },
      productIdentifiers: {
        productIdentifiersArr:
          post.legalForm.productIdentifiers.productIdentifiersArr?.filter(
            (identifier) =>
              identifier.codeProdIdentifiers !== null &&
              identifier.typeProdIdentifiers !== null
          ),
      },
    },
    agents: {
      agentsArr:
        post.agents.agentsArr
          .filter((agent: any) => agent.agentId != null)
          .map((agent: any) => agent.agentId) || [],
    },
    creditProvision: {
      ...post.creditProvision,
      creditPeriodEndDate: DateUtils.getStringFromDateObject(
        post.creditProvision.creditPeriodEndDate as unknown as NgbDate
      ),
    },
    notesAndTasks: {
      notes: post.notesAndTasks.notes?.trim() || null,
      tasks: post.notesAndTasks.tasks?.every(
        (task) => !task.status && !task.dueDate && !task.description
      )
        ? null
        : post.notesAndTasks.tasks,
    },
  } as ProductCaptureData;

  if (output.summary.productType !== 'CLN') {
    delete output.creditProvision;
  }

  delete output.summary?.referenceEntitiesDisplay;

  updateCouponSchedules(output);

  const postData = PRODUCT_POST_DATA.get(output.summary.productType as ProductTypeEnum);

  output = {
    ...output,
    payoff: {
      ...output.payoff,
      ...(postData && { ...postData }),
    },
  };

  return output;
}

function updateCouponSchedules(output: ProductCaptureData): void {
  const { type, schedule } = output.summary.schedule.coupon;

  (schedule as PayoffSchedule[]).forEach((scheduleItem: PayoffSchedule) => {
    if (type === 'SNOWBALL') {
      delete scheduleItem.type;
      scheduleItem.type = type;
    }
  });
}
